import { supabase } from '@/lib/supabase';
import { Song } from '@/types/player';
import { cacheService, CACHE_KEYS } from '@/services/cacheService';
import { isSongFavorited } from '@/services/favorites';
import { getAdaptiveTimeout } from '@/config/performance';
import { testConnection } from '@/lib/supabase';

// TTL do cache: 5 minutos
const CACHE_TTL = 5 * 60 * 1000; // 5 minutos em milliseconds

// Função para buscar uma música por ID com cache
export const getSongById = async (id: number): Promise<Song | null> => {
  const cacheKey = `song_${id}`;
  const cached = await cacheService.get<Song>(cacheKey);
  
  if (cached) {
    return cached;
  }

  try {
    const { data: song, error } = await supabase
      .from('songs')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    if (!song) return null;

    const formattedSong: Song = {
      ...song,
      id: song.id.toString(),
      cover_url: song.capa_url,
      audio_url: song.audio_url,
      is_free: song.is_free
    };

    // Armazena no cache com TTL
    await cacheService.set(cacheKey, formattedSong, CACHE_TTL);

    return formattedSong;
  } catch (error) {
    return null;
  }
};

// Função para buscar todas as músicas com cache
export const getAllSongs = async (): Promise<Song[]> => {
  const cacheKey = CACHE_KEYS.SONGS;
  const cached = await cacheService.get<Song[]>(cacheKey);
  
  if (cached && cached.length > 0) {
    return cached;
  }

  try {
    const { data: songs, error } = await supabase
      .from('songs')
      .select('*')
      .order('id', { ascending: true });

    if (error) throw error;
    if (!songs?.length) return [];

    const formattedSongs: Song[] = songs.map(song => ({
      ...song,
      id: song.id.toString(),
      cover_url: song.capa_url,
      audio_url: song.audio_url,
      is_free: song.is_free
    }));

    // Armazena no cache com TTL
    await cacheService.set(cacheKey, formattedSongs, CACHE_TTL);

    return formattedSongs;
  } catch (error) {
    // Testa conexão quando der erro
    await testConnection();
    
    // Se falhou, tenta usar cache mesmo expirado (busca novamente)
    const expiredCache = await cacheService.get<Song[]>(cacheKey);
    if (expiredCache && expiredCache.length > 0) {
      return expiredCache;
    }
    return [];
  }
};

// 🚀 OTIMIZAÇÃO: Função para buscar músicas por IDs com timeout e cache agressivo
export const getSongsByIds = async (ids: number[]): Promise<Song[]> => {
  if (!ids || ids.length === 0) return [];

  try {
    const { data: songs, error } = await supabase
      .from('songs')
      .select('*')
      .in('id', ids);

    if (error) throw error;
    if (!songs) return [];

    return songs.map(song => ({
      ...song,
      id: song.id.toString(),
      cover_url: song.capa_url,
      audio_url: song.audio_url,
      is_free: song.is_free
    }));
  } catch (error) {
    return [];
  }
};

// Função para buscar músicas por categoria com cache
export const getSongsByCategory = async (category: string): Promise<Song[]> => {
  const cacheKey = `category_${category}`;
  const cached = await cacheService.get<Song[]>(cacheKey);
  
  if (cached && cached.length > 0) {
    return cached;
  }

  try {
    const { data: songs, error } = await supabase
      .from('songs')
      .select('*')
      .eq('category', category)
      .order('id', { ascending: true });

    if (error) throw error;
    if (!songs) return [];

    const formattedSongs: Song[] = songs.map(song => ({
      ...song,
      id: song.id.toString(),
      cover_url: song.capa_url,
      audio_url: song.audio_url,
      is_free: song.is_free
    }));

    // Armazena no cache com TTL
    await cacheService.set(cacheKey, formattedSongs, CACHE_TTL);

    return formattedSongs;
  } catch (error) {
    return [];
  }
};

export const checkSongFavorited = async (songId: number, userId?: string): Promise<boolean> => {
  // Usa a função centralizada de favorites.ts que já tem cache implementado
  return await isSongFavorited(songId, userId);
}; 